import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import PageHeader from './_components/PageHeader';
/* ---------- Icons ---------- */

const I = ({ d, className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon  = (p) => <I {...p} d="M5 12l4 4 10-10" />;

/* ---------- Timeline steps ---------- */

const STEPS = [
  'Order Placed',
  'Card Produced',
  'Quality Check',
  'Dispatched',
  'Out for Delivery',
  'Delivered',
];

/* ---------- Card visual ---------- */

const CardVisual = ({ card }) => {
  return (
    <div className="relative rounded-2xl overflow-hidden bg-[#0b2740] text-white aspect-[1.586/1] p-5 shadow-md">
      {/* Decorative arcs */}
      <span className="absolute -right-16 -top-24 w-56 h-56 rounded-full bg-white/5" />
      <span className="absolute -right-24 top-16 w-56 h-56 rounded-full bg-white/5" />

      {/* Top row: chip + brand */}
      <div className="relative flex items-start justify-between">
        {/* Chip */}
        <div className="w-11 h-8 rounded-md bg-gradient-to-br from-[#f4d97a] to-[#b6892f] relative">
          <span className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-[1px] p-1">
            {[...Array(9)].map((_, i) => (
              <span key={i} className="bg-[#0b2740]/25 rounded-[1px]" />
            ))}
          </span>
        </div>
        <span className="text-[15px] font-extrabold tracking-[0.2em]">FDIC</span>
      </div>

      {/* Number */}
      <div className="relative mt-6 text-[20px] sm:text-[22px] font-semibold tracking-[0.15em]">
        {card.cardNumberMasked || '**** **** **** ----'}
      </div>

      {/* Bottom row: holder + expires + brand logo */}
      <div className="absolute left-5 right-5 bottom-4 flex items-end justify-between">
        <div>
          <div className="text-[9px] tracking-[0.2em] text-white/50 uppercase">Card holder</div>
          <div className="text-[13px] font-semibold tracking-wide uppercase mt-0.5">
            {card.cardholderName || 'Account Holder'}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] tracking-[0.2em] text-white/50 uppercase">Expires</div>
          <div className="text-[13px] font-semibold mt-0.5">{card.expires || '--/--'}</div>
        </div>
        {/* Brand mark (Mastercard-style overlapping circles) */}
        <div className="flex items-center -space-x-2 ml-3">
          <span className="w-5 h-5 rounded-full bg-[#e5493b]" />
          <span className="w-5 h-5 rounded-full bg-[#f5b641]" />
        </div>
      </div>
    </div>
  );
};

/* ---------- Delivery progress card ---------- */

const DeliveryProgress = ({ step }) => {
  const safeStep = Math.max(0, Math.min(5, Number(step) || 0));
  const pct = Math.round((safeStep / 5) * 100);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[15px] font-semibold text-[#1a2b3c]">Delivery Progress</h3>
        <span className="text-[13px] font-semibold text-[#2563eb]">{pct}%</span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-6">
        <div
          className="h-full bg-[#2563eb] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Timeline */}
      <ol className="relative">
        {STEPS.map((label, idx) => {
          const done = idx <= safeStep;
          const isCurrent = idx === safeStep;
          return (
            <li key={label} className="relative flex items-start gap-4 pb-6 last:pb-0">
              {/* Connector */}
              {idx < STEPS.length - 1 && (
                <span
                  className={`absolute left-[11px] top-6 bottom-0 w-0.5 ${
                    idx < safeStep ? 'bg-[#0b2740]' : 'bg-gray-200'
                  }`}
                />
              )}

              {/* Marker */}
              <span
                className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  done
                    ? 'bg-[#2563eb] text-white'
                    : 'bg-white border-2 border-gray-200 text-transparent'
                }`}
              >
                {isCurrent ? (
                  <span className="w-2.5 h-2.5 rounded-full bg-white" />
                ) : (
                  done && <CheckIcon className="w-3.5 h-3.5" />
                )}
              </span>

              {/* Label */}
              <span
                className={`text-[14px] mt-0.5 ${
                  done ? 'text-[#1a2b3c] font-medium' : 'text-gray-400'
                }`}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/* ---------- Delivery details card ---------- */

const DetailRow = ({ label, value, pill }) => (
  <div className="px-5 py-4">
    <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500">{label}</div>
    {pill ? (
      <div className="mt-2">
        <span className="inline-block bg-[#dff3e4] text-[#1f5c3a] text-[11px] font-semibold px-3 py-1 rounded-full">
          {value}
        </span>
      </div>
    ) : (
      <div className="text-[15px] font-semibold text-[#1a2b3c] mt-1.5">{value || '—'}</div>
    )}
  </div>
);

const DeliveryDetails = ({ card }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
    <div className="px-5 pt-5 pb-2">
      <h3 className="text-[15px] font-semibold text-[#1a2b3c]">Delivery details</h3>
    </div>
    <div className="border-t border-gray-100">
      <DetailRow label="Estimated arrival" value={card.estimatedArrival} />
      <div className="border-t border-gray-100" />
      <DetailRow label="Card type" value={card.cardType} />
      <div className="border-t border-gray-100" />
      <DetailRow label="Cardholder" value={card.cardholderName} />
      <div className="border-t border-gray-100" />
      <DetailRow label="Expires" value={card.expires} />
      <div className="border-t border-gray-100" />
      <DetailRow label="Status" value={card.status} pill />
    </div>
  </div>
);

/* ---------- Page ---------- */

const FintechCardDelivery = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/fintech/dashboard');
        if (alive) setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="px-4 pt-5 pb-8 lg:px-8 lg:pt-8 max-w-3xl mx-auto space-y-4">
        <div className="bg-white rounded-2xl h-44 animate-pulse" />
        <div className="bg-white rounded-2xl h-64 animate-pulse" />
        <div className="bg-white rounded-2xl h-72 animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-sm text-red-600">Could not load card details.</div>;
  }

  const card = data.card || {};

  return (
    <div className="px-4 pt-5 pb-8 lg:px-8 lg:pt-8 max-w-3xl mx-auto">
      <PageHeader holderName={data.accountHolderName || ''} />
      {/* Title */}
      <div className="mb-5">
        <h2 className="font-serif text-[24px] leading-tight text-[#1a2b3c]">
          Card Delivery
        </h2>
        <p className="text-[13px] text-gray-500 mt-1">
          Track your new account card shipment
        </p>
      </div>

      {/* Card visual */}
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-2">
          Your new card
        </div>
        <CardVisual card={card} />
      </div>

      {/* Delivery progress */}
      <div className="mb-6">
        <DeliveryProgress step={card.step} />
      </div>

      {/* Delivery details */}
      <DeliveryDetails card={card} />
    </div>
  );
};

export default FintechCardDelivery;